/**
 * ResidentDetail.jsx
 * CCH Care — Phase 2 + 3 + 4 + 5d
 * Last Modified: 2026-05-07
 *
 * Full-screen slide-in view with tabbed content.
 * Allergies displayed in header — compact summary, tap to expand by category.
 *
 * Phase 5d changes:
 * - ADLTab section badge shows "N/A" (muted) instead of "In Progress"
 *   for sections flagged not applicable in the care plan
 * - Header signedCount includes N/A sections (they count as complete)
 * - N/A sections render with a distinct neutral style, not the warning orange
 *
 * 2026-05-04 — Review surface enhancement:
 * - All signed sections (not just hygiene/meals/repositioning) are now
 *   expandable in place to show their saved entries.
 * - Per-section detail renderers read full section data via a new
 *   read-only endpoint (sections-for-record.php). We avoid useADLRecord
 *   here because that hook is designed for active documentation and has
 *   side effects we don't want on a review surface.
 * - Sub-rows visually indented under the parent with consistent horizontal
 *   dividers — matches the Meals pattern.
 * - Single-row sections (Bowel, Urination) render their fields inline;
 *   multi-row sections render an entry list.
 *
 * 2026-05-07 — Dark mode tokenization:
 * - Replaced ~15 hardcoded light-mode color literals with COLORS tokens
 *   that resolve correctly in both themes:
 *     • AllergyBar bg/border (was '#fdf0f0' / 'rgba(231,76,60,0.2)')
 *     • ADL "Completed" pill bg (was '#E8F8F0')
 *     • Signed ADL section card + badge (was '#F0FBF5' /
 *       'rgba(46,204,113,0.15)' / 'rgba(46,204,113,0.3)')
 *     • BedtimeStatusRow signed card + badge (same triplet)
 *     • Vitals signed pill, abnormal banner/card/chips, signed footer
 *       (was '#fff3cd', '#fff8f0', '#f39c12', '#856404', '#d68910',
 *        plus two semi-transparent green literals)
 * - Header bar intentionally left as COLORS.primary — matches AppShell's
 *   brand-blue header in both modes (consistent app-wide chrome).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getResidentDetail } from '../../../api/patients';
import { getVitals, getADLSectionsForRecord } from '../../../api/adl';
import { COLORS, assistLevelLabel } from '../../../config/constants';
import { parseAllergies, formatAllergiesSummary, ALLERGY_CATEGORIES } from '../../admin/components/AllergyPicker';
import { useAuth } from '../../auth/useAuth';
import PatientADLFlagsPanel from '../../adl/PatientADLFlagsPanel';
import { formatTime } from '../../../utils/formatters';
import useTheme from '../../../hooks/useTheme';

const TABS = [
  { key: 'adl',       label: 'ADL Checklist' },
  { key: 'notes',     label: 'Notes'         },
  { key: 'vitals',    label: 'Vitals'        },
  { key: 'care_plan', label: 'Care Plan'     },
];

const RANGES = {
  systolic:         { min: 90,   max: 140,  unit: 'mmHg', label: 'Systolic BP'  },
  diastolic:        { min: 60,   max: 90,   unit: 'mmHg', label: 'Diastolic BP' },
  pulse:            { min: 60,   max: 100,  unit: 'bpm',  label: 'Pulse'        },
  temperature:      { min: 97.0, max: 99.5, unit: '°F',   label: 'Temperature'  },
  o2_saturation:    { min: 95,   max: 100,  unit: '%',    label: 'O₂ Sat'       },
  respiratory_rate: { min: 12,   max: 20,   unit: '/min', label: 'Resp Rate'    },
};

function isAbnormal(field, value) {
  if (value === '' || value === null || value === undefined) return false;
  const v = parseFloat(value);
  const r = RANGES[field];
  return r ? (v < r.min || v > r.max) : false;
}

export default function ResidentDetail({
  patientId, careShiftId, onClose,
  onPrev, onNext, currentIndex, totalCount,
}) {
  const { memberships } = useAuth();
  const role = memberships?.[0]?.role ?? '';
  const isSupervisor = ['Care Supervisor', 'Care Admin'].includes(role);
  const { isDark, toggleTheme } = useTheme();

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('adl');

  useEffect(() => {
    if (!patientId || !careShiftId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    getResidentDetail(patientId, careShiftId)
      .then(res => { if (!cancelled && res.success) setData(res); })
      .catch(err => console.error('Detail fetch error:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, careShiftId]);

  const [touchStartX, setTouchStartX] = useState(null);
  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX;
    if (diff > 80 && onPrev) onPrev();
    else if (diff < -80 && onNext) onNext();
    setTouchStartX(null);
  };

  const resident     = data?.resident;
  const alerts       = data?.alerts       || [];
  const adlSections  = data?.adl_sections || [];
  const adlChecklist = data?.adl_checklist || [];
  const bedtime      = data?.bedtime      ?? null;
  const notes        = data?.notes        || [];
  const carePlan     = data?.care_plan    || [];

  const [vitalsSignature, setVitalsSignature] = useState(null);
  useEffect(() => {
    const adlRecordId = data?.adl_record?.adl_record_id;
    if (!adlRecordId) { setVitalsSignature(null); return; }
    getVitals(adlRecordId)
      .then(d => { if (d.success) setVitalsSignature(d.signature || null); })
      .catch(() => {});
  }, [data?.adl_record?.adl_record_id]);

  const adlSignedCount = adlSections.filter(s => s.signed || s.is_na).length;
  const vitalsIsSigned = !!vitalsSignature;
  const signedCount    = adlSignedCount + (vitalsIsSigned ? 1 : 0);
  const totalSections  = data?.adl_record ? 9 : adlSections.length;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: COLORS.bg, zIndex: 100, display: 'flex', flexDirection: 'column', animation: 'slideIn 0.25s ease-out' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header — intentionally COLORS.primary in both modes, matches AppShell */}
      <div style={{ background: COLORS.primary, color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: '4px 8px' }}>←</button>

        {loading ? <span>Loading...</span> : (
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{resident?.full_name}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Rm {resident?.room_number}
              {resident?.bed_label ? ` · ${resident.bed_label}` : ''}
              {resident?.age ? ` · ${resident.age}y` : ''}
              {resident?.gender ? ` · ${resident.gender}` : ''}
            </div>
          </div>
        )}

        {!loading && data?.adl_record && totalSections > 0 && (
          <div style={{
            padding: '4px 10px', borderRadius: 20,
            background: signedCount === totalSections ? COLORS.accentAlpha : 'rgba(255,255,255,0.15)',
            fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap',
          }}>
            {signedCount}/{totalSections} complete
          </div>
        )}

        {/* Dark mode toggle — the slide-in covers AppShell entirely
            (position: fixed; inset: 0), so without this users can't
            change theme while reviewing a resident. Style mirrors
            AppShell's toggle for visual consistency. */}
        <button
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8,
            color: '#fff', fontSize: 15, cursor: 'pointer',
            padding: '4px 8px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 'unset', width: 32, height: 32,
          }}
        >
          {isDark ? '☀️' : '🌙'}
        </button>

        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={onPrev} disabled={!onPrev} style={{ ...navBtnStyle, opacity: onPrev ? 1 : 0.3 }}>‹</button>
          <button onClick={onNext} disabled={!onNext} style={{ ...navBtnStyle, opacity: onNext ? 1 : 0.3 }}>›</button>
        </div>
      </div>

      {totalCount > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, padding: '8px 0', background: COLORS.surface }}>
          {Array.from({ length: totalCount }, (_, i) => (
            <div key={i} style={{
              width: i === currentIndex ? 16 : 6, height: 6, borderRadius: 3,
              background: i === currentIndex ? COLORS.primary : COLORS.border, transition: 'all 0.2s',
            }} />
          ))}
        </div>
      )}

      {alerts.length > 0 && (
        <div style={{ display: 'flex', gap: 6, padding: '8px 16px', background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, flexWrap: 'wrap' }}>
          {alerts.map((a, i) => (
            <span key={i} style={{
              padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
              background: a.severity === 'critical' ? COLORS.dangerLight : COLORS.warningLight,
              color: a.severity === 'critical' ? COLORS.danger : COLORS.warning,
            }}>
              {a.alert_label}
            </span>
          ))}
        </div>
      )}

      {!loading && resident?.allergies && (
        <AllergyBar allergies={resident.allergies} />
      )}

      <div style={{ display: 'flex', background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            flex: 1, padding: '12px 8px', background: 'none', border: 'none',
            borderBottom: `3px solid ${activeTab === tab.key ? COLORS.primary : 'transparent'}`,
            color: activeTab === tab.key ? COLORS.primary : COLORS.textMuted,
            fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading resident details...</div>
        ) : (
          <>
            {activeTab === 'adl' && (
              <ADLTab
                adlRecord={data?.adl_record}
                adlSections={adlSections}
                checklist={adlChecklist}
                bedtime={bedtime}
                patientId={patientId}
              />
            )}
            {activeTab === 'notes'     && <NotesTab notes={notes} />}
            {activeTab === 'vitals'    && <VitalsTab adlRecordId={data?.adl_record?.adl_record_id} initialSignature={vitalsSignature} />}
            {activeTab === 'care_plan' && (
              <CarePlanTab
                items={carePlan}
                patientId={patientId}
                patientName={resident?.full_name ?? ''}
                isSupervisor={isSupervisor}
              />
            )}
          </>
        )}
      </div>

      {data?.adl_record?.status !== 'completed' && (
        <button style={{
          position: 'absolute', bottom: 24, right: 24,
          width: 56, height: 56, borderRadius: '50%',
          border: 'none', background: COLORS.primary, color: '#fff',
          fontSize: 16, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(27,107,147,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          ▶ ADL
        </button>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}

// ── Allergy Bar ──────────────────────────────────────────────
function AllergyBar({ allergies }) {
  const [expanded, setExpanded] = useState(false);
  const summary = formatAllergiesSummary(allergies);
  if (!summary) return null;

  const data = parseAllergies(allergies);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setExpanded(e => !e)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setExpanded(v => !v)}
      style={{
        background: COLORS.dangerLight,
        borderBottom: `1px solid ${COLORS.dangerAlpha}`,
        cursor: 'pointer', userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px' }}>
        <span style={{ fontSize: 14 }}>⚠️</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger }}>ALLERGIES</span>
        {!expanded && (
          <span style={{ fontSize: 12, color: COLORS.danger, flex: 1 }}>
            {summary.length > 60 ? summary.slice(0, 57) + '…' : summary}
          </span>
        )}
        <span style={{
          fontSize: 11, color: COLORS.danger, marginLeft: 'auto',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s',
        }}>▼</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ALLERGY_CATEGORIES.map(cat => {
            const items = data[cat.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 13, marginTop: 1 }}>{cat.icon}</span>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.danger, textTransform: 'uppercase' }}>
                    {cat.label}:
                  </span>
                  <span style={{ fontSize: 12, color: COLORS.danger, marginLeft: 6 }}>
                    {items.join(', ')}
                  </span>
                </div>
              </div>
            );
          })}
          {data.other?.trim() && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 13, marginTop: 1 }}>🔤</span>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.danger, textTransform: 'uppercase' }}>Other:</span>
                <span style={{ fontSize: 12, color: COLORS.danger, marginLeft: 6 }}>{data.other}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ADL Tab ──────────────────────────────────────────────────
function ADLTab({ adlRecord, adlSections, checklist, bedtime, patientId }) {
  const [expanded, setExpanded] = useState(null);
  const [sections, setSections] = useState({});
  const [sectionsLoading, setSectionsLoading] = useState(false);

  // Fetch full section data via the read-only endpoint. Lightweight and
  // safe for a review surface (no side effects, unlike useADLRecord).
  useEffect(() => {
    const adlRecordId = adlRecord?.adl_record_id;
    if (!adlRecordId) { setSections({}); return; }

    let cancelled = false;
    setSectionsLoading(true);
    getADLSectionsForRecord(adlRecordId)
      .then(d => {
        if (cancelled) return;
        if (d.success) setSections(d.sections || {});
      })
      .catch(err => console.error('Sections fetch error:', err))
      .finally(() => { if (!cancelled) setSectionsLoading(false); });
    return () => { cancelled = true; };
  }, [adlRecord?.adl_record_id]);

  if (!adlRecord) {
    return (
      <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
        <div>No ADL record started for this shift yet.</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Tap &ldquo;Start ADL&rdquo; to begin documenting.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'inline-block', fontSize: 12, fontWeight: 600,
        padding: '4px 10px', borderRadius: 20, marginBottom: 16,
        background: adlRecord.status === 'completed' ? COLORS.accentLight : COLORS.warningLight,
        color: adlRecord.status === 'completed' ? COLORS.accent : COLORS.warning,
      }}>
        {adlRecord.status === 'completed' ? '✅ Completed' : '🔄 In Progress'}
      </div>

      {adlSections.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {adlSections.map(sec => {
            const isExpanded  = expanded === sec.section;
            // All signed (non-N/A) sections become clickable for review.
            const isClickable = sec.signed && !sec.is_na;

            const badgeConfig = sec.signed
              ? { label: '✓ Signed',     bg: COLORS.accentAlpha,  color: COLORS.accent    }
              : sec.is_na
                ? { label: 'N/A',         bg: COLORS.bg,           color: COLORS.textMuted }
                : { label: 'In Progress', bg: COLORS.warningLight, color: COLORS.warning   };

            const cardBg     = sec.signed ? COLORS.accentLight : COLORS.surface;
            const cardBorder = sec.signed ? COLORS.accentAlpha  : COLORS.border;

            return (
              <div key={sec.section} style={{
                borderRadius: 10,
                border: `1px solid ${cardBorder}`,
                background: cardBg,
                overflow: 'hidden',
                opacity: sec.is_na ? 0.7 : 1,
              }}>
                <div
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={() => isClickable && setExpanded(isExpanded ? null : sec.section)}
                  onKeyDown={isClickable ? e => (e.key === 'Enter' || e.key === ' ') && setExpanded(isExpanded ? null : sec.section) : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                    cursor: isClickable ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ fontSize: 18 }}>{sec.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{sec.label}</div>
                    {sec.signed && (
                      <div style={{ fontSize: 11, color: COLORS.accent, marginTop: 1 }}>
                        Signed by {decodeHtml(sec.signed_by)} · {formatTime(sec.signed_at)}
                      </div>
                    )}
                    {sec.is_na && !sec.signed && sec.na_reason && (
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
                        {sec.na_reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: badgeConfig.bg, color: badgeConfig.color,
                    }}>
                      {badgeConfig.label}
                    </span>
                    {isClickable && (
                      <span style={{ fontSize: 12, color: COLORS.textMuted }}>{isExpanded ? '▲' : '▼'}</span>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <SectionDetails
                    section={sec.section}
                    data={sections?.[sec.section]}
                    loading={sectionsLoading}
                  />
                )}
              </div>
            );
          })}
          {/* 9th row — Bedtime checklist (per-resident-per-day, distinct cadence from ADL) */}
          </div>
        ) : (
          <ChecklistFallback checklist={checklist} />
        )}

        {/* Sleep & Wellness — once-per-day, distinct cadence from per-shift ADL.
            Separate section to reinforce the conceptual difference and to keep
            the bedtime CTA visible regardless of whether an ADL record exists. */}
        {adlSections.length > 0 && (
          <>
            <div style={{
              marginTop: 20, marginBottom: 8,
              fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              Sleep &amp; Wellness · Once per day
            </div>
            <BedtimeStatusRow bedtime={bedtime} patientId={patientId} />
          </>
        )}
    </div>
  );
}

// ── Per-section detail rendering ─────────────────────────────
// Indented under the parent header (left padding 44px = icon column + gap),
// rows separated by faint horizontal lines. Matches the Meals pattern.
function SectionDetails({ section, data, loading }) {
  if (loading && !data) {
    return (
      <div style={{
        borderTop: `1px solid ${COLORS.border}`,
        padding: '8px 14px 8px 44px',
        fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic',
      }}>
        Loading…
      </div>
    );
  }
  const renderer = SECTION_RENDERERS[section];
  if (!renderer) return null;
  return (
    <div style={{
      borderTop: `1px solid ${COLORS.border}`,
      padding: '4px 14px 8px 44px',
    }}>
      {renderer(data)}
    </div>
  );
}

const SECTION_RENDERERS = {
  hygiene:       renderHygiene,
  meals:         renderMeals,
  repositioning: renderRepositioning,
  fluid_intake:  renderFluidIntake,
  bowel:         renderBowel,
  urination:     renderUrination,
  skin_checks:   renderSkinChecks,
  safety_checks: renderSafetyChecks,
};

function normalizeEntries(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return Object.values(data);
}

function titleCase(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function joinMeta(parts) {
  return parts.filter(Boolean).join(' · ');
}

function isTruthy(v) {
  // MySQL bools come back as 0/1 ints or '0'/'1' strings. Normalize.
  return !!v && v !== '0' && v !== 0;
}

function isFalsy(v) {
  return v === false || v === 0 || v === '0';
}

function renderHygiene(data) {
  const entries = normalizeEntries(data);
  // Only show tasks that have actually been documented (have an assistance_level
  // or were refused). Empty placeholder rows stay hidden.
  const documented = entries.filter(e => e.assistance_level || isTruthy(e.refused));
  if (documented.length === 0) return <EmptyDetail label="No hygiene tasks documented" />;
  return documented.map((e, i, arr) => (
    <DetailRow
      key={e.adl_hygiene_id ?? i}
      isLast={i === arr.length - 1}
      title={titleCase(e.task_type)}
      meta={joinMeta([
        e.assistance_level && `Assist: ${assistLevelLabel(e.assistance_level)}`,
        isTruthy(e.refused) && 'Refused',
        e.performed_by_name && decodeHtml(e.performed_by_name),
        e.performed_at && formatTime(e.performed_at),
      ])}
      notes={e.notes}
    />
  ));
}

function renderMeals(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No meals documented" />;
  return entries.map((e, i, arr) => {
    const consumed = isTruthy(e.refused)
      ? 'Refused'
      : (e.percent_consumed != null ? `${e.percent_consumed}% consumed` : '—');
    return (
      <DetailRow
        key={e.adl_meal_id ?? i}
        isLast={i === arr.length - 1}
        title={`${titleCase(e.meal_type)} · ${consumed}`}
        meta={joinMeta([
          e.assist_level && `Assist: ${assistLevelLabel(e.assist_level)}`,
          e.performed_by_name && decodeHtml(e.performed_by_name),
          e.performed_at && formatTime(e.performed_at),
        ])}
        notes={e.notes}
      />
    );
  });
}

function renderRepositioning(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No repositioning entries" />;
  return entries.map((e, i, arr) => {
    const flags = [];
    if (isTruthy(e.was_refused)) flags.push('Refused');
    // skin_intact false/0 means a problem worth flagging
    if (isFalsy(e.skin_intact))  flags.push('Skin issue noted');
    return (
      <DetailRow
        key={e.adl_repo_id ?? i}
        isLast={i === arr.length - 1}
        title={titleCase(e.position) || 'Repositioning'}
        meta={joinMeta([
          e.assist_level && `Assist: ${assistLevelLabel(e.assist_level)}`,
          flags.join(', '),
          e.performed_by_name && decodeHtml(e.performed_by_name),
          e.performed_at && formatTime(e.performed_at),
        ])}
        notes={e.notes}
      />
    );
  });
}

function renderFluidIntake(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No fluid intake recorded" />;
  return entries.map((e, i, arr) => {
    const amount = isTruthy(e.refused)
      ? 'Refused'
      : (e.amount_ml ? `${e.amount_ml} ml` : '—');
    return (
      <DetailRow
        key={e.adl_fluid_id ?? i}
        isLast={i === arr.length - 1}
        title={`${titleCase(e.fluid_type) || 'Fluid'} · ${amount}`}
        meta={joinMeta([
          e.performed_by_name && decodeHtml(e.performed_by_name),
          e.performed_at && formatTime(e.performed_at),
        ])}
        notes={e.notes}
      />
    );
  });
}

function renderBowel(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No bowel record" />;
  // Bowel is single-row per shift
  const e = entries[0];
  const facts = [];
  if (isTruthy(e.had_bm))            facts.push('Had BM');
  if (isFalsy(e.had_bm))             facts.push('No BM this shift');
  if (e.consistency)                 facts.push(titleCase(e.consistency));
  if (isTruthy(e.incontinent))       facts.push('Incontinent');
  return (
    <DetailRow
      isLast={true}
      title={facts.join(' · ') || 'Documented'}
      meta={joinMeta([
        e.performed_by_name && decodeHtml(e.performed_by_name),
        e.performed_at && formatTime(e.performed_at),
      ])}
      notes={e.notes}
    />
  );
}

function renderUrination(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No urination record" />;
  // Urination is single-row per shift
  const e = entries[0];
  const facts = [];
  if (e.output_type)            facts.push(titleCase(e.output_type));
  if (e.urine_color)            facts.push(`Color: ${titleCase(e.urine_color)}`);
  if (e.incontinent_episodes)   facts.push(`${e.incontinent_episodes} incont. episode${parseInt(e.incontinent_episodes) === 1 ? '' : 's'}`);
  return (
    <DetailRow
      isLast={true}
      title={facts.join(' · ') || 'Documented'}
      meta={joinMeta([
        e.performed_by_name && decodeHtml(e.performed_by_name),
        e.performed_at && formatTime(e.performed_at),
      ])}
      notes={e.notes}
    />
  );
}

function renderSkinChecks(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No skin checks documented" />;
  return entries.map((e, i, arr) => {
    const flags = [];
    if (isTruthy(e.wound_care_performed)) flags.push('Wound care performed');
    if (isTruthy(e.nurse_notified))       flags.push('Nurse notified');
    return (
      <DetailRow
        key={e.adl_skin_check_id ?? i}
        isLast={i === arr.length - 1}
        title={`${titleCase(e.body_location) || 'Body location'} · ${titleCase(e.result) || 'Documented'}`}
        meta={joinMeta([
          flags.join(', '),
          e.performed_by_name && decodeHtml(e.performed_by_name),
          e.performed_at && formatTime(e.performed_at),
        ])}
        notes={e.description || e.notes}
      />
    );
  });
}

function renderSafetyChecks(data) {
  const entries = normalizeEntries(data);
  if (entries.length === 0) return <EmptyDetail label="No safety checks documented" />;
  return entries.map((e, i, arr) => {
    const checked = isTruthy(e.is_checked);
    return (
      <DetailRow
        key={e.adl_safety_check_id ?? i}
        isLast={i === arr.length - 1}
        title={`${checked ? '✓' : '✗'} ${e.item_label || `Item ${e.checklist_item_id}`}`}
        meta={null}
        notes={e.notes}
      />
    );
  });
}

function DetailRow({ title, meta, notes, isLast }) {
  return (
    <div style={{
      padding: '8px 0',
      borderBottom: isLast ? 'none' : `1px solid ${COLORS.border}`,
    }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}>{title}</div>
      {meta && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{meta}</div>}
      {notes && <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 2 }}>{notes}</div>}
    </div>
  );
}

function EmptyDetail({ label }) {
  return (
    <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic', padding: '4px 0' }}>
      {label || 'No entries'}
    </div>
  );
}

// ── Legacy fallback for shifts with no ADL record ────────────
function ChecklistFallback({ checklist }) {
  const categories = {};
  checklist.forEach(item => {
    const cat = item.category || 'other';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(item);
  });
  const catLabels = { hygiene: 'Hygiene', nutrition: 'Nutrition', mobility: 'Mobility', other: 'Other' };
  return (
    <div>
      {Object.entries(categories).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>
            {catLabels[cat] || cat}
          </div>
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${COLORS.border}` }}>
              <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{item.completed ? '✅' : '☐'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text }}>{item.label}</div>
                {item.completed && (
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {item.performed_by} · {formatTime(item.performed_at)}
                    {item.assistance_level ? ` · ${assistLevelLabel(item.assistance_level)}` : ''}
                  </div>
                )}
                {item.notes && <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic', marginTop: 2 }}>{item.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function NotesTab({ notes }) {
  if (notes.length === 0) return <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>No notes for this resident.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {notes.map((note, i) => (
        <div key={i} style={{ padding: 12, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
              {note.author_name}
              <span style={{ fontWeight: 400, color: COLORS.textMuted }}> · {note.author_role} · {note.shift_name}</span>
            </span>
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>{formatTime(note.created_at)}</span>
          </div>
          {note.priority && note.priority !== 'normal' && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
              background: note.priority === 'urgent' ? COLORS.dangerLight : COLORS.warningLight,
              color: note.priority === 'urgent' ? COLORS.danger : COLORS.warning,
              textTransform: 'uppercase', marginBottom: 4, display: 'inline-block',
            }}>
              {note.priority}
            </span>
          )}
          <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.5 }}>{note.note_text}</div>
        </div>
      ))}
    </div>
  );
}

function VitalsTab({ adlRecordId, initialSignature }) {
  const [readings, setReadings]   = useState([]);
  const [signature, setSignature] = useState(initialSignature);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    if (!adlRecordId) { setLoading(false); return; }
    setLoading(true);
    getVitals(adlRecordId)
      .then(data => {
        if (!data.success) throw new Error(data.error || 'Failed to load vitals');
        setReadings(data.readings || []);
        setSignature(data.signature || null);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [adlRecordId]);

  if (!adlRecordId) {
    return (
      <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🩺</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>No ADL record yet</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Start the ADL flow sheet to document vitals.</div>
      </div>
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>Loading vitals…</div>;

  if (error) return (
    <div style={{ textAlign: 'center', padding: 32, color: COLORS.danger }}>
      <div style={{ fontSize: 13 }}>⚠️ {error}</div>
    </div>
  );

  if (readings.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🩺</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>No vitals recorded this shift</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Open the ADL flow sheet to add vitals readings.</div>
      </div>
    );
  }

  const abnormalCount = readings.filter(r => !!r.has_abnormal).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
          {readings.length} reading{readings.length !== 1 ? 's' : ''} this shift
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
          background: signature ? COLORS.accentAlpha : COLORS.warningLight,
          color: signature ? COLORS.accent : COLORS.warning,
        }}>
          {signature ? '✓ Signed' : 'Not signed'}
        </span>
      </div>

      {abnormalCount > 0 && (
        <div style={{
          background: COLORS.warningLight,
          border: `1px solid ${COLORS.warning}`,
          borderRadius: 8, padding: '8px 12px', marginBottom: 12,
          fontSize: 13, color: COLORS.warning, fontWeight: 600,
        }}>
          ⚠ {abnormalCount} reading{abnormalCount !== 1 ? 's' : ''} with out-of-range values
        </div>
      )}

      {readings.map(r => {
        const time = formatTime(r.recorded_at);
        const vitals = [
          (r.systolic || r.diastolic) ? {
            label: 'BP', value: `${r.systolic ?? '—'}/${r.diastolic ?? '—'}`, unit: 'mmHg',
            abnormal: isAbnormal('systolic', r.systolic) || isAbnormal('diastolic', r.diastolic),
          } : null,
          r.pulse            != null ? { label: 'Pulse',     value: r.pulse,            unit: 'bpm',  abnormal: isAbnormal('pulse', r.pulse) }            : null,
          r.temperature      != null ? { label: 'Temp',      value: parseFloat(r.temperature).toFixed(1), unit: '°F', abnormal: isAbnormal('temperature', r.temperature) } : null,
          r.o2_saturation    != null ? { label: 'O₂ Sat',    value: r.o2_saturation,    unit: '%',    abnormal: isAbnormal('o2_saturation', r.o2_saturation) }    : null,
          r.respiratory_rate != null ? { label: 'Resp Rate', value: r.respiratory_rate, unit: '/min', abnormal: isAbnormal('respiratory_rate', r.respiratory_rate) } : null,
        ].filter(Boolean);

        return (
          <div key={r.adl_vitals_id} style={{
            background: !!r.has_abnormal ? COLORS.warningLight : COLORS.surface,
            border: `1px solid ${!!r.has_abnormal ? COLORS.warning : COLORS.border}`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted }}>{time}</span>
              {!!r.has_abnormal && (
                <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.warning, background: COLORS.warningAlpha, padding: '2px 6px', borderRadius: 4 }}>
                  ⚠ ABNORMAL
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {vitals.map((v, i) => (
                <div key={i} style={{
                  background: v.abnormal ? COLORS.warningAlpha : COLORS.bg,
                  border: `1px solid ${v.abnormal ? COLORS.warning : COLORS.border}`,
                  borderRadius: 6, padding: '4px 10px', fontSize: 12,
                }}>
                  <span style={{ color: COLORS.textMuted, marginRight: 4 }}>{v.label}</span>
                  <span style={{ fontWeight: 700, color: v.abnormal ? COLORS.warning : COLORS.text }}>{v.value}</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 10, marginLeft: 2 }}>{v.unit}</span>
                  {v.abnormal && <span style={{ marginLeft: 4 }}>⚠</span>}
                </div>
              ))}
            </div>
            {r.notes && <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>{r.notes}</p>}
            <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '4px 0 0' }}>— {r.recorded_by_name}</p>
          </div>
        );
      })}

      {signature && (
        <div style={{
          marginTop: 8, padding: '8px 12px', borderRadius: 8,
          background: COLORS.accentLight,
          border: `1px solid ${COLORS.accentAlpha}`,
          fontSize: 12, color: COLORS.accent,
        }}>
          ✓ Signed by {decodeHtml(signature.signed_by_name)} · {formatTime(signature.signed_at)}
        </div>
      )}
    </div>
  );
}

function CarePlanTab({ items, patientId, patientName, isSupervisor }) {
  const [showFlags, setShowFlags] = useState(false);

  return (
    <div>
      {isSupervisor && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showFlags ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>ADL Section Applicability</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                Configure which ADL sections apply to this resident
              </div>
            </div>
            <button
              onClick={() => setShowFlags(f => !f)}
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 8,
                background: showFlags ? COLORS.primary : 'none',
                color: showFlags ? '#fff' : COLORS.primary,
                border: `1px solid ${COLORS.primary}`,
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              {showFlags ? 'Done' : 'Manage'}
            </button>
          </div>

          {showFlags && <PatientADLFlagsPanel patientId={patientId} patientName={patientName} />}

          {!showFlags && (
            <div style={{
              marginTop: 8, padding: '8px 12px', borderRadius: 8,
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              fontSize: 12, color: COLORS.textMuted,
            }}>
              Tap Manage to configure N/A sections for this resident.
            </div>
          )}
        </div>
      )}

      {isSupervisor && items.length > 0 && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, marginBottom: 16 }} />
      )}

      {items.length === 0 && !isSupervisor ? (
        <div style={{ textAlign: 'center', padding: 32, color: COLORS.textMuted }}>No care plan details available.</div>
      ) : items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Care Plan Notes
          </div>
          {items.map((item, i) => (
            <div key={i} style={{ padding: 12, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', marginBottom: 4 }}>{item.section}</div>
              <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.4 }}>{item.detail}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function decodeHtml(str) {
  if (!str) return str;
  return str
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

const navBtnStyle = {
  width: 32, height: 32, borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.3)',
  background: 'rgba(255,255,255,0.1)',
  color: '#fff', fontSize: 18, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ── Bedtime status row ───────────────────────────────────────
// Clickable CTA in ADLTab's Sleep & Wellness section. Deep-links
// to /dashboard/bedtime?patient_id=X which auto-selects the resident
// in BedtimeChecklistPage. Per-resident-per-day cadence — distinct
// from the 8 shift-bound ADL sections rendered above it.
function BedtimeStatusRow({ bedtime, patientId }) {
  const navigate = useNavigate();
  const isSigned  = bedtime?.status === 'completed';
  const isStarted = bedtime?.bedtime_checklist_id != null;

  const badgeConfig = isSigned
    ? { label: '✓ Signed',     bg: COLORS.accentAlpha,  color: COLORS.accent    }
    : isStarted
      ? { label: 'In Progress', bg: COLORS.warningLight, color: COLORS.warning   }
      : { label: 'Not Started', bg: COLORS.bg,           color: COLORS.textMuted };

  const cardBg     = isSigned ? COLORS.accentLight : COLORS.surface;
  const cardBorder = isSigned ? COLORS.accentAlpha  : COLORS.border;

  const handleOpen = () => {
    if (!patientId) return;
    navigate(`/dashboard/bedtime?patient_id=${patientId}`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOpen();
        }
      }}
      style={{
        borderRadius: 10,
        border: `1px solid ${cardBorder}`,
        background: cardBg,
        overflow: 'hidden',
        cursor: patientId ? 'pointer' : 'default',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
      }}>
        <span style={{ fontSize: 18 }}>🌙</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>Bedtime Checklist</div>
          {isSigned ? (
            <div style={{ fontSize: 11, color: COLORS.accent, marginTop: 1 }}>
              Signed by {decodeHtml(bedtime.signed_by)} · {formatTime(bedtime.signed_at)}
            </div>
          ) : isStarted ? (
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
              Documentation started · tap to continue
            </div>
          ) : (
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
              Tap to document tonight&apos;s bedtime routine
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: badgeConfig.bg, color: badgeConfig.color,
          }}>
            {badgeConfig.label}
          </span>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>›</span>
        </div>
      </div>
    </div>
  );
}